import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from '@rtkwlf/ui';
import { ShareLink } from '../../../contact/ShareLink';
import { memoizedCredentialSchemaSelector } from '../../../../selectors/credentialSchemaSelectors';
import { ROUTE_PATH } from '../../../../constants';
import RouterNavLink from '../../../../widgets/RouterNavLink';
import { useRedirectCallback } from '../../../../hooks';
import { ACCOUNT_TYPE_TOOLTIP } from '../constants';
import { useFetchSubscriptionsOnMount } from '../useFetchSubscriptions';
import { credentialSelector } from '../../../../reducers/credential';
import { featureFlagSelector } from '../../../../reducers/session';
import { CredentialSchema } from '../../../../types';
import styled from 'styled-components';

export const ListCloudApps = styled.div`
  display: block;
`;

export const DisabledNavLink = styled(RouterNavLink)`
  background: #d3d3d3;
`;

export const AppSelection: React.FunctionComponent = () => {
  useFetchSubscriptionsOnMount();
  const subscriptions = useSelector(credentialSelector.getSubscriptions);
  const onGoBack = useRedirectCallback(
    ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN,
  );

  const [purposeType, setPurposeType] = React.useState<string>('log');

  const logSchemas = useSelector(
    memoizedCredentialSchemaSelector.getVisibleLogSchemas,
  );
  const scanSchemas = useSelector(
    memoizedCredentialSchemaSelector.getVisibleScanSchemas,
  );
  const authSchemas = useSelector(
    memoizedCredentialSchemaSelector.getVisibleAuthSchemas,
  );

  const itsmSchemas = useSelector(
    memoizedCredentialSchemaSelector.getVisibleITSMSchemas,
  );

  const isListCloudAppsEnabled = useSelector(
    featureFlagSelector.credentials.listCloudApps,
  );

  const handleSelectChange = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.value;
      setPurposeType(value);
    },
    [setPurposeType],
  );

  const schemas = React.useMemo<CredentialSchema[]>(() => {
    let selectedSchemas: CredentialSchema[] = [];
    if (purposeType === 'log') {
      selectedSchemas = logSchemas;
    } else if (purposeType === 'scan') {
      selectedSchemas = scanSchemas;
    } else if (purposeType === 'auth') {
      selectedSchemas = authSchemas;
    } else if (purposeType === 'itsm') {
      selectedSchemas = itsmSchemas;
    }

    return selectedSchemas;
  }, [scanSchemas, logSchemas, authSchemas, itsmSchemas, purposeType]);
  const paid = React.useMemo<CredentialSchema[]>(() => {
    return schemas.filter((s) => !s.no_charge);
  }, [schemas]);
  const free = React.useMemo<CredentialSchema[]>(() => {
    return schemas.filter((s) => s.no_charge);
  }, [schemas]);

  const purchased = React.useMemo<CredentialSchema[]>(() => {
    let purchased: CredentialSchema[] = [];
    if (isListCloudAppsEnabled && subscriptions) {
      subscriptions.forEach((subscription) => {
        let sensors: [string, number][] = [];
        if (subscription?.managedDetectionResponse?.cloudSensors) {
          sensors = Object.entries(
            subscription.managedDetectionResponse.cloudSensors,
          );
        }
        sensors.forEach((sensor) => {
          const name = sensor[0];
          const quantity = sensor[1];
          const filtered: CredentialSchema[] = paid.filter(
            (schema) =>
              (schema.name.toLowerCase().includes(name.toLowerCase()) ||
                schema.display_name
                  .toLowerCase()
                  .includes(name.toLowerCase())) &&
              quantity > 0,
          );
          purchased.push(...filtered);
        });
      });
    }
    return purchased;
  }, [subscriptions, isListCloudAppsEnabled, paid]);

  const notPurchased = React.useMemo<CredentialSchema[]>(() => {
    return paid.filter((s) => !purchased.includes(s));
  }, [purchased, paid]);

  return (
    <>
      <p className="mt-3">
        To add an account, select the appropriate cloud application. Services
        not included in your subscription are available at an additional cost.
        Please{' '}
        <ShareLink subject={'Trouble adding cloud accounts'}>
          contact your CST
        </ShareLink>{' '}
        if you have questions.
      </p>
      <p>
        To update an existing account, please go{' '}
        <Link to={ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN}>back</Link>{' '}
        to the main page and click the pencil icon beside the account you want
        to modify.
      </p>

      <h5 className="mt-3">
        Account Type
        {ACCOUNT_TYPE_TOOLTIP}
      </h5>
      <div className="my-2">
        <label>
          <input
            type="radio"
            className="mr-2"
            checked={purposeType === 'log'}
            onChange={handleSelectChange}
            value="log"
            data-cy="log-type"
          />
          Cloud Detection and Response
        </label>
      </div>
      <div className="my-2">
        <label>
          <input
            type="radio"
            className="mr-2"
            checked={purposeType === 'scan'}
            onChange={handleSelectChange}
            data-cy="scan-type"
            value="scan"
          />
          Cloud Security Posture Management
        </label>
      </div>
      {authSchemas.length > 0 && (
        <div className="my-2">
          <label>
            <input
              type="radio"
              className="mr-2"
              checked={purposeType === 'auth'}
              onChange={handleSelectChange}
              data-cy="auth-type"
              value="auth"
            />
            Federated Authentication
          </label>
        </div>
      )}
      <div className="my-2">
        <label>
          <input
            type="radio"
            className="mr-2"
            checked={purposeType === 'itsm'}
            onChange={handleSelectChange}
            value="itsm"
            data-cy="itsm-type"
          />
          ITSM Ticketing Integration
        </label>
      </div>
      <h5 className="mt-3 mb-3">Cloud Applications</h5>
      {!isListCloudAppsEnabled && (
        <div className="cloud-application-card-deck">
          {schemas.map((schema) => (
            <RouterNavLink
              key={schema.name}
              data-cy={schema.name}
              className="cloud-application-card hover-shadow"
              to={`${ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.NEW}/${schema.name}`}
            >
              <img src={`/static/${schema.image}`} alt={schema.display_name} />
            </RouterNavLink>
          ))}
        </div>
      )}
      {isListCloudAppsEnabled && (
        <ListCloudApps>
          {!!purchased.length && <h6>Purchased</h6>}
          <div
            className="cloud-application-card-deck"
            data-cy={'purchased-schemas'}
          >
            {purchased.map((schema) => (
              <RouterNavLink
                key={schema.name}
                data-cy={`${schema.name}-purchased`}
                className="cloud-application-card hover-shadow"
                to={`${ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.NEW}/${schema.name}`}
              >
                <img
                  src={`/static/${schema.image}`}
                  alt={schema.display_name}
                />
              </RouterNavLink>
            ))}
          </div>
          {!!free.length && <h6>Included</h6>}
          <div className="cloud-application-card-deck" data-cy={'free-schemas'}>
            {free.map((schema) => (
              <RouterNavLink
                key={schema.name}
                data-cy={`${schema.name}-free`}
                className="cloud-application-card hover-shadow"
                to={`${ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.NEW}/${schema.name}`}
              >
                <img
                  src={`/static/${schema.image}`}
                  alt={schema.display_name}
                />
              </RouterNavLink>
            ))}
          </div>
          {!!notPurchased.length && <h6>Not Purchased</h6>}
          <div
            className="cloud-application-card-deck"
            data-cy={'available-schemas'}
          >
            {notPurchased.map((schema) => (
              <DisabledNavLink
                key={schema.name}
                data-cy={`${schema.name}-available`}
                aria-disabled={true}
                className="disabled-link disabledCursor cloud-application-card hover-shadow"
                to={ROUTE_PATH.CONTACT_CST}
              >
                <img
                  src={`/static/${schema.image}`}
                  alt={schema.display_name}
                />
              </DisabledNavLink>
            ))}
          </div>
        </ListCloudApps>
      )}
      <p className="my-4">
        <Button ghost onClick={onGoBack} data-cy="back">
          Back
        </Button>
      </p>
    </>
  );
};
